import { onCleanup, onMount, Show } from 'solid-js';

interface NumberInputProps {
  value: string;
  onInput: (value: string) => void;
  prefix?: string;
  min?: number;
  max?: number;
  step?: number;
  required?: boolean;
  placeholder?: string;
}

export default function NumberInput(props: NumberInputProps) {
  const step = () => props.step ?? 1;
  let inputRef!: HTMLInputElement;

  function clamp(n: number): number {
    if (props.min !== undefined && n < props.min) return props.min;
    if (props.max !== undefined && n > props.max) return props.max;
    return n;
  }

  function stepBy(delta: number) {
    const current = parseFloat(props.value) || 0;
    const next = clamp(current + delta);
    props.onInput(String(next));
  }

  function onWheel(e: WheelEvent) {
    e.preventDefault();
    stepBy(e.deltaY < 0 ? step() : -step());
  }

  onMount(() => inputRef.addEventListener('wheel', onWheel, { passive: false }));
  onCleanup(() => inputRef.removeEventListener('wheel', onWheel));

  return (
    <div class={`ch-numinput${props.prefix ? ' has-prefix' : ''}`}>
      <Show when={props.prefix}>
        <span class="ch-numinput-prefix">{props.prefix}</span>
      </Show>
      <input
        ref={inputRef}
        class="ch-numinput-field"
        type="number"
        value={props.value}
        onInput={(e) => props.onInput(e.currentTarget.value)}
        min={props.min}
        max={props.max}
        // `step` drives OUR steppers and the wheel. As a DOM attribute it would make the
        // browser refuse any value off the grid (a copied 50 under min 1 / step 5) and block
        // the form's submit silently when the field sits in a collapsed section.
        step="any"
        required={props.required}
        placeholder={props.placeholder}
      />
      <div class="ch-numinput-stepper">
        <button type="button" tabIndex={-1} onClick={() => stepBy(step())} aria-label="Increase">
          <svg fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path d="M18 15l-6-6-6 6" />
          </svg>
        </button>
        <button type="button" tabIndex={-1} onClick={() => stepBy(-step())} aria-label="Decrease">
          <svg fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>
    </div>
  );
}
